import { FlashcardsService } from "@/client"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { FlashcardSaveNoteModal } from "@/components/Flashcards/FlashcardSaveNoteModal"
import { FlashcardShortcutsModal } from "@/components/Flashcards/FlashcardShortcutsModal"
import { PreparingNextCard } from "@/components/Flashcards/PreparingNextCard"
import { StudyHelpButton } from "@/components/Flashcards/StudyHelpButton"
import { StudySessionHero } from "@/components/Flashcards/StudySessionHero"
import { useColorModeValue } from "@/components/ui/color-mode"
import { Box, Button, Grid, HStack, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"

export type StudySessionCard = {
  id: string
  deck_id: string
  deck_name?: string
  front: string
  back: string
  is_new?: boolean
}

export type StudySessionProps = {
  cards: StudySessionCard[]
  modeLabel: string
  onComplete: (xpEarned: number) => void
  onProgress?: (index: number, xpEarned: number) => void
  initialIndex?: number
  initialXp?: number
}

const RATINGS = [1, 2, 3, 4, 5] as const
type Rating = (typeof RATINGS)[number]

const RATING_CONFIG: Record<
  Rating,
  {
    label: string
    xp: string
    colorPalette: string
    variant: "solid" | "outline"
  }
> = {
  1: {
    label: "Not at all",
    xp: "+1 XP",
    colorPalette: "red",
    variant: "outline",
  },
  2: {
    label: "Barely",
    xp: "+1 XP",
    colorPalette: "orange",
    variant: "outline",
  },
  3: {
    label: "Somewhat",
    xp: "+2 XP",
    colorPalette: "yellow",
    variant: "outline",
  },
  4: { label: "Mostly", xp: "+3 XP", colorPalette: "green", variant: "solid" },
  5: {
    label: "Perfectly!",
    xp: "+5 XP",
    colorPalette: "teal",
    variant: "solid",
  },
}

function playSuccessSound() {
  try {
    type AudioCtxCtor = typeof AudioContext
    const AudioCtxClass = (window.AudioContext ||
      (window as Window & { webkitAudioContext?: AudioCtxCtor })
        .webkitAudioContext) as AudioCtxCtor | undefined
    if (!AudioCtxClass) return
    const ctx = new AudioCtxClass()
    const notes = [523.25, 659.25, 783.99, 1046.5]
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = "sine"
      const t = ctx.currentTime + i * 0.11
      osc.frequency.setValueAtTime(freq, t)
      gain.gain.setValueAtTime(0.22, t)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25)
      osc.start(t)
      osc.stop(t + 0.3)
    })
  } catch {
    // AudioContext not supported or blocked
  }
}

export function StudySession({
  cards,
  modeLabel,
  onComplete,
  onProgress,
  initialIndex = 0,
  initialXp = 0,
}: StudySessionProps) {
  const queryClient = useQueryClient()

  const cardBg = useColorModeValue("white", "gray.900")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const progressTrack = useColorModeValue("gray.200", "gray.700")
  const answerBg = useColorModeValue("green.50", "green.950")
  const answerBorder = useColorModeValue("green.200", "green.800")
  const answerText = useColorModeValue("gray.900", "gray.50")
  const streakBg = useColorModeValue("orange.50", "orange.950")
  const streakBorder = useColorModeValue("orange.200", "orange.800")

  const [index, setIndex] = useState(initialIndex)
  const [revealed, setRevealed] = useState(false)
  const [xpEarned, setXpEarned] = useState(initialXp)
  const [streak, setStreak] = useState(0)
  const [showStreakBurst, setShowStreakBurst] = useState(false)
  const [isVerySmallScreen] = useState(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(max-width: 399px)").matches
  })
  const [isPreparingNextCard, setIsPreparingNextCard] = useState(false)
  const [isSaveNoteOpen, setIsSaveNoteOpen] = useState(false)

  const reviewMutation = useMutation({
    mutationFn: ({ cardId, rating }: { cardId: string; rating: Rating }) =>
      FlashcardsService.reviewCard({ cardId, requestBody: { rating } }),
    onSuccess: async (data, variables) => {
      const newXp = xpEarned + data.xp_earned
      setXpEarned(newXp)

      if (variables.rating >= 4) {
        const newStreak = streak + 1
        setStreak(newStreak)
        if (newStreak >= 3 && newStreak % 3 === 0) {
          setShowStreakBurst(true)
          setTimeout(() => setShowStreakBurst(false), 2000)
        }
      } else {
        setStreak(0)
      }

      const invalidations = [
        queryClient.invalidateQueries({ queryKey: ["my-xp"] }),
        queryClient.invalidateQueries({ queryKey: ["flashcards-due-summary"] }),
      ]

      const isSessionComplete = index + 1 >= cards.length
      if (isSessionComplete) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ["flashcards-due"] }),
          queryClient.invalidateQueries({ queryKey: ["flashcards-study"] }),
        )
      }
      await Promise.all(invalidations)

      if (isSessionComplete) {
        onComplete(newXp)
      } else {
        const nextIndex = index + 1
        setIndex(nextIndex)
        setRevealed(false)
        onProgress?.(nextIndex, newXp)
      }
      setIsPreparingNextCard(false)
    },
    onError: () => {
      setIsPreparingNextCard(false)
    },
  })

  const card = cards[index]
  const progressPct = Math.round((index / cards.length) * 100)

  const handleRate = useCallback(
    (rating: Rating) => {
      if (isPreparingNextCard) return
      if (rating === 5) playSuccessSound()
      setIsPreparingNextCard(true)
      reviewMutation.mutate({ cardId: card.id, rating })
    },
    [isPreparingNextCard, reviewMutation, card.id],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const isTypingTarget =
        target?.isContentEditable ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select"
      // Focused buttons/links keep their native Space/Enter activation.
      const isInteractiveTarget = tag === "button" || tag === "a"
      if (
        isTypingTarget ||
        isInteractiveTarget ||
        isPreparingNextCard ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return

      if (event.key === " ") {
        event.preventDefault()
        if (!revealed) {
          setRevealed(true)
        }
        return
      }

      if (event.key.toLowerCase() === "b") {
        event.preventDefault()
        setIsSaveNoteOpen(true)
        return
      }

      const rating = Number(event.key) as Rating
      if (revealed && RATINGS.includes(rating)) {
        event.preventDefault()
        handleRate(rating)
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [revealed, isPreparingNextCard, handleRate])

  return (
    <Grid
      data-testid="study-session-layout"
      templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) 320px" }}
      gap={{ base: 4, lg: 6 }}
      alignItems="start"
      maxW="1280px"
      mx="auto"
      w="full"
    >
      <Box as="main" aria-label="Flashcard review" w="full" minW={0}>
        {/* Streak burst */}
        {showStreakBurst && (
          <Box
            bg={streakBg}
            borderWidth={1}
            borderColor={streakBorder}
            borderRadius="lg"
            p={3}
            mb={3}
            textAlign="center"
          >
            <Text fontWeight="bold" color="orange.500">
              🔥 {streak}-card streak! Keep it up!
            </Text>
          </Box>
        )}

        {/* Flashcard */}
        {isPreparingNextCard ? (
          <PreparingNextCard borderColor={borderColor} mutedText={mutedText} />
        ) : (
          <>
            <Box
              bg={cardBg}
              borderWidth={2}
              borderColor={revealed ? answerBorder : borderColor}
              borderRadius="2xl"
              p={6}
              mb={4}
              minH="220px"
              boxShadow={revealed ? "0 8px 32px rgba(22, 163, 74, 0.12)" : "sm"}
              transition="border-color 0.3s ease, box-shadow 0.3s ease"
            >
              <HStack justify="space-between" align="start" mb={3} gap={3}>
                <Text
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color={mutedText}
                  fontWeight="semibold"
                >
                  Question
                </Text>
                <FlashcardSaveNoteModal
                  card={card}
                  compact
                  open={isSaveNoteOpen}
                  onOpenChange={setIsSaveNoteOpen}
                />
              </HStack>
              <Box fontSize="lg" fontWeight="medium" mb={revealed ? 5 : 0}>
                <FlashcardContent content={card.front} />
              </Box>

              {revealed && (
                <>
                  <Box borderTopWidth={1} borderColor={borderColor} my={4} />
                  <Box
                    bg={answerBg}
                    borderWidth={1}
                    borderColor={answerBorder}
                    borderRadius="xl"
                    p={5}
                  >
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="green.600"
                      _dark={{ color: "green.400" }}
                      fontWeight="bold"
                      mb={3}
                    >
                      Answer
                    </Text>
                    <Box fontSize="xl" fontWeight="bold" color={answerText}>
                      <FlashcardContent content={card.back} />
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            {/* Controls */}
            {!revealed ? (
              <Button
                width="full"
                size="lg"
                colorPalette="teal"
                onClick={() => setRevealed(true)}
              >
                Show Answer
              </Button>
            ) : (
              <>
                <Text
                  fontSize="sm"
                  textAlign="center"
                  color={mutedText}
                  mb={3}
                  fontWeight="medium"
                >
                  How well did you know this?
                </Text>
                <HStack gap={2} mb={2}>
                  {RATINGS.map((rating) => {
                    const cfg = RATING_CONFIG[rating]
                    return (
                      <Button
                        key={rating}
                        aria-label={`${rating} ${cfg.label} ${cfg.xp}`}
                        flex={1}
                        colorPalette={cfg.colorPalette}
                        variant={cfg.variant}
                        size="sm"
                        h="auto"
                        py={3}
                        px={1}
                        onClick={() => handleRate(rating)}
                        disabled={reviewMutation.isPending}
                      >
                        <VStack gap={isVerySmallScreen ? 1 : 0.5}>
                          <Text
                            fontWeight="extrabold"
                            fontSize="lg"
                            lineHeight={1}
                          >
                            {rating}
                          </Text>
                          {!isVerySmallScreen && (
                            <Text
                              fontSize="xs"
                              lineHeight={1.3}
                              textAlign="center"
                            >
                              {cfg.label}
                            </Text>
                          )}
                          <Text fontSize="xs" opacity={0.75}>
                            {cfg.xp}
                          </Text>
                        </VStack>
                      </Button>
                    )
                  })}
                </HStack>
                <HStack justify="space-between" px={1}>
                  <Text fontSize="xs" color={mutedText}>
                    Not at all
                  </Text>
                  <Text fontSize="xs" color={mutedText}>
                    Perfectly
                  </Text>
                </HStack>
              </>
            )}
          </>
        )}
      </Box>

      <StudySessionHero
        modeLabel={modeLabel}
        totalCards={cards.length}
        currentCard={index + 1}
        xpEarned={xpEarned}
        streak={streak}
        progressPct={progressPct}
        progressTrack={progressTrack}
        deckName={card.deck_name}
        actions={
          <>
            <FlashcardShortcutsModal />
            <StudyHelpButton />
          </>
        }
        compactActions={
          <>
            <FlashcardShortcutsModal compact />
            <StudyHelpButton />
          </>
        }
      />
    </Grid>
  )
}
